import stringSimilarity from 'string-similarity';

// Function to fetch similarity scores from the backend
async function fetchSimilarityScores(designChallenge) {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/ai/compute-similarity-scores`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ design_challenge: designChallenge }),
    });
    const data = await response.json();
    return data; // Contains similarities for programs, methods, and tools
  } catch (error) {
    console.error('Error fetching similarity scores:', error);
    return null;
  }
}

// Core function to preprocess text for analysis
export const preprocessText = (text) => {
  if (Array.isArray(text)) {
    text = text.join(" ");
  }

  if (typeof text !== "string") {
    console.log("preprocessText: Expected a string, but got:", text);
    return [];
  }

  const stopWords = [
    "and", "or", "the", "is", "to", "for", "in", "on", "with", "as"
  ];
  const words = text.toLowerCase().split(/\s+/);
  const filteredWords = words.filter(
    (word) => !stopWords.includes(word) && word.length > 1
  );
  return filteredWords;
};

// Function to map complexity strings to numeric scores for comparison
function mapComplexityToScore(complexity) {
  const complexityMap = {
    simple: 1,
    medium: 2,
    complex: 3,
  };
  return complexityMap[complexity.toLowerCase()] || 1;
}

// Function to detect complexity from design challenge text
export const detectComplexity = (designChallenge) => {
  const complexityKeywords = {
    simple: 1,
    basic: 1,
    straightforward: 1,
    moderate: 2,
    intermediate: 2,
    challenging: 3,
    complex: 3,
    complicated: 3,
    "multi-phased": 3,
    advanced: 3,
  };

  for (const [keyword, multiplier] of Object.entries(complexityKeywords)) {
    if (designChallenge.toLowerCase().includes(keyword.toLowerCase())) {
      return multiplier;
    }
  }

  return 2.0; // Default complexity
};

// Function to detect urgency from design challenge text
export const detectUrgency = (designChallenge) => {
  const urgencyKeywords = {
    '1 week': 1,
    '2 weeks': 1,
    quickly: 1,
    weeks: 2,
    months: 3,
    asap: 1,
    immediate: 2,
    minor: 1,
    urgent: 1,
    quick: 1,
    fast: 2,
    deadline: 2,
    rapid: 1,
    rapidly: 1,
  };

  for (const [keyword, multiplier] of Object.entries(urgencyKeywords)) {
    if (designChallenge.toLowerCase().includes(keyword.toLowerCase())) {
      return multiplier;
    }
  }

  return 1.0; // Default urgency
};

// Enhanced function to determine tool duration based on complexity score and urgency
export const determineToolDuration = (tool, complexityScore, urgencyMultiplier) => {
  if (!tool) return 1;

  // Use enhanced duration calculation if available
  let lowDuration = parseFloat(tool.low_duration) || parseFloat(tool.duration) || 1;
  let highDuration = parseFloat(tool.high_duration) || lowDuration;

  let adjustedDuration = highDuration;

  if (urgencyMultiplier > 1) {
    adjustedDuration /= urgencyMultiplier;
  }

  let complexityAdjustment = (highDuration - lowDuration) * complexityScore;
  adjustedDuration -= complexityAdjustment;

  if (adjustedDuration > highDuration) {
    adjustedDuration = highDuration;
  } else if (adjustedDuration < lowDuration) {
    adjustedDuration = lowDuration;
  }

  return Math.max(1, Math.round(adjustedDuration));
};

// Advanced function to filter relevant tools with sophisticated scoring
export const filterRelevantTools = (tools, designChallenge, stages, methods) => {
  if (!Array.isArray(tools)) {
    console.warn("filterRelevantTools: tools is not an array");
    return [];
  }

  const normalizedStages = stages.map((stage) => stage.toLowerCase());
  const normalizedProgramMethods = methods.map((method) => method.toLowerCase());

  // For each tool, compute a sophisticated score
  const toolsWithScores = tools.map((tool) => {
    const toolStage = tool.related_stage ? tool.related_stage.toLowerCase() : '';
    const stagePass = normalizedStages.includes(toolStage);

    const toolDescription = tool.description ? tool.description.toLowerCase() : '';
    const descriptionMatchScore = stringSimilarity.compareTwoStrings(
      designChallenge.toLowerCase(),
      toolDescription
    );

    const toolName = tool.tool ? tool.tool.toLowerCase() : '';
    const nameMatchScore = stringSimilarity.compareTwoStrings(
      designChallenge.toLowerCase(),
      toolName
    );

    const complexityMatch =
      detectComplexity(designChallenge) >= mapComplexityToScore(tool.complexity || 'simple');

    // Method match (strict)
    const toolMethods = tool.related_methods
      ? tool.related_methods.split(',').map((method) => method.trim().toLowerCase())
      : [];
    const methodMatch = toolMethods.some((toolMethod) => normalizedProgramMethods.includes(toolMethod));

    // Similarity score from backend
    const similarityScore = tool.similarity_score || 0;

    // Compute a total score with weights
    const stageWeight = 2.0;
    const descriptionWeight = 0.5;
    const nameWeight = 0.5;
    const complexityWeight = 0.3;
    const methodWeight = 1.0;
    const similarityWeight = 3.0; // Increased weight for similarity

    let totalScore = 0;
    if (stagePass) totalScore += stageWeight;
    totalScore += descriptionMatchScore * descriptionWeight;
    totalScore += nameMatchScore * nameWeight;
    if (complexityMatch) totalScore += complexityWeight;
    if (methodMatch) totalScore += methodWeight;
    totalScore += similarityScore * similarityWeight;

    // Exclude tools with no method match
    if (!methodMatch) {
      totalScore = 0; // Or assign a penalty score
    }

    return {
      ...tool,
      totalScore,
    };
  });

  // Sort tools by totalScore
  toolsWithScores.sort((a, b) => b.totalScore - a.totalScore);

  // Select top tools based on a threshold
  const topScore = toolsWithScores.length > 0 ? toolsWithScores[0].totalScore : 0;
  const percentageGap = 0.07; // Adjust this value to see the effect
  const threshold = topScore * (1 - percentageGap);

  const topTools = toolsWithScores.filter((tool) => tool.totalScore >= threshold);

  return topTools;
};

// Function to filter deliverables based on design challenge and program of work
function filterRelevantDeliverables(deliverables, designChallenge, programOfWork) {
  if (!Array.isArray(deliverables)) return [];

  const preprocessedChallenge = preprocessText(designChallenge);

  return deliverables.filter((deliverable) => {
    let isRelevantToChallenge = false;
    let isRelevantToProgram = deliverable.related_program_of_work && 
                              deliverable.related_program_of_work.includes(programOfWork);

    const deliverableDescription = deliverable.description
      ? deliverable.description.toLowerCase()
      : '';
    const deliverableTitle = deliverable.deliverable ? deliverable.deliverable.toLowerCase() : '';

    // Use preprocessedChallenge here
    preprocessedChallenge.forEach((keyword) => {
      if (
        deliverableDescription.includes(keyword) ||
        deliverableTitle.includes(keyword)
      ) {
        isRelevantToChallenge = true;
      }
    });

    return isRelevantToChallenge && isRelevantToProgram;
  });
}

// Function to perform fuzzy matching for keywords with a logarithmic scale
function fuzzyKeywordMatch(programKeywords, designChallenge) {
  const preprocessedChallenge = preprocessText(designChallenge);

  // Split program keywords by spaces as well, to handle multi-word phrases
  const programKeywordList = programKeywords
    ? programKeywords.toLowerCase().split(',').flatMap(keyword => keyword.trim().split(' '))
    : [];

  // Calculate fuzzy match for each keyword
  const fuzzyMatches = programKeywordList
    .map((keyword) => {
      const matchScore = preprocessedChallenge.some(
        (word) => stringSimilarity.compareTwoStrings(word, keyword.trim()) > 0.6 // Reduced threshold for fuzzy match
      );
      return matchScore ? 1 : 0; // Binary match (1 for match, 0 for no match)
    })
    .reduce((acc, curr) => acc + curr, 0); // Count total matches

  // Apply logarithmic scaling to reduce the impact of large numbers of matches
  const logKeywordMatch = Math.log(1 + fuzzyMatches);

  return logKeywordMatch; // Return the logarithmic keyword match score
}

// Function to calculate program length and organize tools by stages
export const calculateProgramLengthAndStages = (relevantTools) => {
  const stages = {};

  if (!Array.isArray(relevantTools)) {
    return stages;
  }

  relevantTools.forEach((tool) => {
    const stageName = tool.related_stage || 'Default Stage';
    if (!stages[stageName]) {
      stages[stageName] = { tools: [], totalDuration: 0 };
    }
    stages[stageName].tools.push(tool);
    stages[stageName].totalDuration += parseFloat(tool.duration) || 0;
  });

  return stages;
};

// Main function to generate activity plan with enhanced AI scoring
const generateActivityPlan = async (
  designChallenge,
  programsOfWork,
  methods,
  tools,
  deliverables
) => {
  console.log("🎯 generateActivityPlan called with sophisticated AI scoring:", {
    designChallenge: designChallenge?.substring(0, 50) + "...",
    programsCount: programsOfWork?.length || 0,
    methodsCount: methods?.length || 0,
    toolsCount: tools?.length || 0,
    deliverablesCount: deliverables?.length || 0,
  });

  if (!designChallenge || !programsOfWork || programsOfWork.length === 0) {
    console.error('Missing design challenge or programs of work data.');
    return {
      selectedPrograms: [],
      finalProgramSequence: [],
    };
  }

  try {
    // Fetch AI similarity scores from backend
    const similarityData = await fetchSimilarityScores(designChallenge);

    // Detect complexity and urgency
    const complexityScore = detectComplexity(designChallenge);
    const urgencyMultiplier = detectUrgency(designChallenge);

    console.log("📊 Analysis scores:", { complexityScore, urgencyMultiplier });

    // Map similarity scores to programs and tools if available
    let programsWithScores = programsOfWork;
    let toolsWithScores = tools;

    if (similarityData && similarityData.programs) {
      console.log("🤖 Using AI similarity scores from backend");

      programsWithScores = programsOfWork.map((program) => {
        const similarityEntry = similarityData.programs.find(
          (s) => s.program_of_work === program.program_of_work
        );
        return {
          ...program,
          similarity_score: similarityEntry ? similarityEntry.similarity_score : 0,
        };
      });

      toolsWithScores = tools.map((tool) => {
        const similarityEntry = similarityData.tools.find(
          (s) => s.tool === tool.tool
        );
        return {
          ...tool,
          similarity_score: similarityEntry ? similarityEntry.similarity_score : 0,
        };
      });
    } else {
      console.log("⚠️ Falling back to local scoring (no AI similarity data)");
      // Add default similarity scores for fallback
      programsWithScores = programsOfWork.map(program => ({
        ...program,
        similarity_score: 0
      }));
      toolsWithScores = tools.map(tool => ({
        ...tool,
        similarity_score: 0
      }));
    }

    // Enhanced program scoring with multiple weighted factors
    const semanticSimilarityWeight = 3.0;
    const programMatchWeight = 1.0;
    const keywordMatchWeight = 0.4;
    const focusMatchWeight = 0.6;
    const descriptionMatchWeight = 0.4;
    const outcomeMatchWeight = 0.5;

    const designChallengeLower = designChallenge.toLowerCase();
    const lemmatizedChallenge = similarityData?.lemmatized_challenge || designChallenge;

    const scoredPrograms = programsWithScores.map((program) => {
      // Exclude 'Preparation' and 'Implementation' phases from scoring
      if (program.phase === 'Preparation' || program.phase === 'Implementation') {
        return {
          ...program,
          score: 0,
        };
      }

      // Similarity scores using string-similarity library
      const programMatch = program && program.program_of_work
        ? stringSimilarity.compareTwoStrings(
            designChallengeLower,
            program.program_of_work.toLowerCase()
          )
        : 0;

      const descriptionMatch = program.description
        ? stringSimilarity.compareTwoStrings(
            designChallengeLower,
            program.description.toLowerCase()
          )
        : 0;

      const outcomeMatch = program.outcome
        ? stringSimilarity.compareTwoStrings(
            designChallengeLower,
            program.outcome.toLowerCase()
          )
        : 0;

      // Keyword and focus matches with fuzzy logic
      const keywordMatch = fuzzyKeywordMatch(program.keywords, lemmatizedChallenge);

      const focusMatch = program.Focus
        ? program.Focus.toLowerCase().split(',').reduce(
            (acc, focus) => acc + (designChallengeLower.includes(focus.trim()) ? 1 : 0),
            0
          )
        : 0;

      // Calculate total score with sophisticated weighting
      let score =
        program.similarity_score * semanticSimilarityWeight +
        programMatch * programMatchWeight +
        keywordMatch * keywordMatchWeight +
        focusMatch * focusMatchWeight +
        descriptionMatch * descriptionMatchWeight +
        outcomeMatch * outcomeMatchWeight;

      // Apply urgency and complexity multipliers
      const complexityMultiplier = complexityScore / 2.0; // Convert complexity score to multiplier
      score *= urgencyMultiplier * complexityMultiplier;

      return {
        ...program,
        score,
      };
    });

    // Sort and select programs based on sophisticated scoring
    scoredPrograms.sort((a, b) => b.score - a.score);

    console.log('🎯 Program Scores:');
    scoredPrograms.forEach((program) => {
      console.log(
        `Program: ${program.program_of_work}, Score: ${program.score.toFixed(4)}, Phase: ${program.phase}`
      );
    });

    // Calculate weighted program selection based on percentage gap
    const percentageGap = 0.05;
    const topScore = scoredPrograms[0]?.score || 0;
    let selectedPrograms = scoredPrograms.filter(
      (p) =>
        p.phase !== 'Preparation' &&
        p.phase !== 'Implementation' &&
        topScore - p.score <= topScore * percentageGap
    );

    // Ensure at least one program is selected
    if (selectedPrograms.length === 0 && scoredPrograms.length > 0) {
      const highestScoringProgram = scoredPrograms[0];
      if (highestScoringProgram) {
        console.log(
          `No programs met the threshold, including highest-scoring: ${highestScoringProgram.program_of_work}`
        );
        selectedPrograms = [highestScoringProgram];
      }
    }

    // Build final program sequence with proper phase ordering
    const finalProgramSequence = [];

    // Always include Project Preparation if available
    const preparationProgram = scoredPrograms.find((p) => p.phase === 'Preparation');
    if (preparationProgram) {
      finalProgramSequence.push(preparationProgram);
    }

    let lastPhaseAdded = null;

    // Add selected programs in order of their phases
    const phases = ['Preparation', 'Strategy', 'Discovery', 'Implementation', 'Design', 'Development'];

    phases.forEach((phase) => {
      const programsInPhase = selectedPrograms.filter((program) => program.phase === phase);
      if (programsInPhase.length > 0) {
        // For the Discovery phase, sort according to desired order
        if (phase === 'Discovery') {
          const desiredOrder = ['Insight Sprint', 'Digital Discovery', 'CX Sprint', 'Design Sprint'];
          programsInPhase.sort((a, b) => {
            const indexA = desiredOrder.indexOf(a.program_of_work);
            const indexB = desiredOrder.indexOf(b.program_of_work);
            return (
              (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) - 
              (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB)
            );
          });
        }
        programsInPhase.forEach((program) => {
          finalProgramSequence.push(program);
          lastPhaseAdded = phase;
        });
      }
    });

    // Handle Implementation phase insertion intelligently
    const implementationProgram = scoredPrograms.find((p) => p.phase === 'Implementation');
    const hasDiscoveryOrStrategy = selectedPrograms.some(
      (p) => p.phase === 'Discovery' || p.phase === 'Strategy'
    );
    const hasDesign = selectedPrograms.some((p) => p.phase === 'Design');

    if (implementationProgram && !finalProgramSequence.find(p => p.phase === 'Implementation')) {
      if (!hasDiscoveryOrStrategy && hasDesign) {
        const designIndex = finalProgramSequence.findIndex((p) => p.phase === 'Design');
        if (designIndex !== -1) {
          finalProgramSequence.splice(designIndex + 1, 0, implementationProgram);
        }
      } else {
        const insertIndex = lastPhaseAdded
          ? finalProgramSequence.map((p) => p.phase).lastIndexOf(lastPhaseAdded) + 1
          : finalProgramSequence.length;
        finalProgramSequence.splice(insertIndex, 0, implementationProgram);
      }
    }

    // Process each program with enhanced tool selection and deliverables
    finalProgramSequence.forEach((program) => {
      // Parse program fields
      if (typeof program.Stages === 'string') {
        program.Stages = program.Stages.split(',').map((stage) => stage.trim());
      } else if (!Array.isArray(program.Stages)) {
        program.Stages = ['Implementation'];
      }

      if (typeof program.related_methods === 'string') {
        program.related_methods = program.related_methods.split(',').map((method) => method.trim());
      } else if (!Array.isArray(program.related_methods)) {
        program.related_methods = [];
      }

      // Use enhanced tool filtering
      const relevantTools = filterRelevantTools(
        toolsWithScores,
        designChallenge,
        program.Stages,
        program.related_methods
      );

      // Apply enhanced duration calculation
      relevantTools.forEach((tool) => {
        tool.duration = determineToolDuration(tool, complexityScore, urgencyMultiplier);
      });

      // Calculate tools by stages
      const stages = calculateProgramLengthAndStages(relevantTools);
      program.toolsByStage = stages;

      // Calculate total program length
      program.totalProgramLength = Object.values(stages).reduce(
        (total, stage) => total + stage.totalDuration,
        0
      ) || program.duration || 40;

      // Filter relevant deliverables
      if (deliverables && deliverables.length > 0) {
        const relevantDeliverables = filterRelevantDeliverables(
          deliverables,
          designChallenge,
          program.program_of_work
        );
        program.expectedDeliverables = relevantDeliverables.map(d => d.deliverable || d.title);
      } else {
        program.expectedDeliverables = [];
      }

      // Ensure required fields
      program.isIncluded = true;
      program.methods = program.related_methods;
    });

    console.log("✅ Enhanced AI-powered program generation completed:", {
      selectedProgramsCount: selectedPrograms.length,
      finalSequenceCount: finalProgramSequence.length,
      usedAIScoring: !!similarityData
    });

    return {
      selectedPrograms: finalProgramSequence,
      finalProgramSequence: finalProgramSequence,
    };

  } catch (error) {
    console.error("❌ Error in enhanced generateActivityPlan:", error);
    // Fallback to simplified generation
    return {
      selectedPrograms: [],
      finalProgramSequence: [],
    };
  }
};

export default generateActivityPlan;